/**
 * The standing-order editor (M6 plan P29, spec §6.1): preset, order, ROE, every parameter of the order, the stop
 * conditions, save / revert / close. It renders an `EditorState` and dispatches `EditorAction`s; all logic is in
 * `orderModel.ts`. The focused row carries `data-focused` (keyboard and pad focus are the model's). UI events only.
 */
import type { ReactNode } from 'react'
import {
  choicesOf,
  editorRows,
  isDirty,
  listOf,
  numberText,
  ordersFor,
  paramValue,
  presetsFor,
  validateOrder,
  type EditorAction,
  type EditorOptions,
  type EditorRow,
  type EditorState,
  type OrderEditorData,
} from './orderModel'

export interface OrderEditorProps {
  state: EditorState
  data: OrderEditorData
  opts: EditorOptions
  /** Display labels of the orders (fleetScreen.json `orders`). */
  orderLabels: Readonly<Record<string, string>>
  dispatch: (a: EditorAction) => void
}

function focusAttr(on: boolean): { 'data-focused'?: '' } {
  return on ? { 'data-focused': '' } : {}
}

function rowKey(r: EditorRow): string {
  switch (r.kind) {
    case 'stop':
      return `stop-${r.stop}`
    case 'number':
      return `param-${r.param}`
    case 'enum':
    case 'multi':
    case 'ref':
      return `field-${r.field}`
    case 'action':
      return `action-${r.action}`
    default:
      return r.kind
  }
}

export function OrderEditor(p: OrderEditorProps): ReactNode {
  const { state: s, data, opts } = p
  const labels = data.config.labels
  const rows = editorRows(data, s.draft)
  const issues = validateOrder(data, s.draft, opts)
  const bad = new Set<string>(issues.map((i) => i.field))
  const presets = presetsFor(data, opts.shipClass)
  const dirty = isDirty(data, s)

  function title(r: EditorRow): string {
    switch (r.kind) {
      case 'preset':
        return 'Preset'
      case 'order':
        return 'Order'
      case 'roe':
        return 'Rules of engagement'
      case 'stop':
        return labels.stops[r.stop]
      case 'stopDay':
        return labels.params.stopDay ?? 'Stop on day'
      case 'number':
        return labels.params[r.param] ?? r.param
      case 'enum':
      case 'multi':
      case 'ref':
        return labels.refs[r.field]
      case 'action':
        return ''
    }
  }

  function value(r: EditorRow, focused: boolean, index: number): ReactNode {
    const stepAt = (dir: -1 | 1): void => {
      p.dispatch({ type: 'cursor', index })
      p.dispatch({ type: 'step', dir })
    }
    const d = s.draft
    switch (r.kind) {
      case 'preset':
        return (
          <button type="button" tabIndex={-1} className="sui-btn" data-testid="order-preset-apply" onClick={() => p.dispatch({ type: 'applyPreset' })}>
            ◂ {presets[s.presetPick]?.name ?? '—'} ▸ · Apply
          </button>
        )
      case 'order':
        return (
          <span className="oe-cycle">
            {ordersFor(data, opts.shipClass).map((o) => (
              <button key={o} type="button" tabIndex={-1} className="sui-btn" aria-pressed={d.order === o} onClick={() => p.dispatch({ type: 'setOrder', order: o })}>
                {p.orderLabels[o] ?? o}
              </button>
            ))}
          </span>
        )
      case 'roe':
        return (
          <span className="oe-cycle">
            {(Object.keys(labels.roe) as (keyof typeof labels.roe)[]).map((x) => (
              <button key={x} type="button" tabIndex={-1} className="sui-btn" aria-pressed={d.roe === x} onClick={() => p.dispatch({ type: 'setRoe', roe: x })}>
                {labels.roe[x]}
              </button>
            ))}
          </span>
        )
      case 'stop': {
        const on = d.stops.includes(r.stop)
        return (
          <button type="button" tabIndex={-1} className="sui-btn" aria-pressed={on} onClick={() => p.dispatch({ type: 'toggleStop', stop: r.stop })}>
            {on ? '☑ On' : '☐ Off'}
          </button>
        )
      }
      case 'stopDay':
      case 'number': {
        const k = r.kind === 'stopDay' ? 'stopDay' : r.param
        const v = paramValue(data, d, k)
        const set = r.kind === 'stopDay' ? d.stopDay !== undefined : d.params[k] !== undefined
        const lim = data.orders.limits[k]
        return (
          <span className="oe-num">
            <input
              type="number"
              tabIndex={-1}
              className="oe-input"
              aria-label={title(r)}
              value={Number.isFinite(v) ? v : ''}
              min={lim?.[0]}
              max={lim?.[1]}
              step={data.config.steps[k] ?? 1}
              onChange={(e) => p.dispatch({ type: 'setNumber', param: k, value: e.currentTarget.value === '' ? Number.NaN : Number(e.currentTarget.value) })}
            />
            <span className="oe-dim">
              {numberText(k, v)}
              {set ? '' : ' (default)'}
              {lim !== undefined ? ` · ${lim[0]}–${lim[1]}` : ''}
            </span>
          </span>
        )
      }
      case 'enum': {
        const names = r.field === 'sellPolicy' ? labels.sellPolicy : labels.patrolRule
        const cur = r.field === 'sellPolicy' ? d.sellPolicy : d.patrolRule
        return (
          <span className="oe-cycle">
            {Object.entries(names).map(([k, name]) => (
              <button key={k} type="button" tabIndex={-1} className="sui-btn" aria-pressed={cur === k} onClick={() => p.dispatch({ type: 'setEnum', field: r.field, value: k })}>
                {name}
              </button>
            ))}
          </span>
        )
      }
      case 'ref': {
        const choices = choicesOf(opts, d.order, r.field)
        const cur = d[r.field]
        const name = cur === undefined ? 'None' : (choices.find((c) => c.id === cur)?.name ?? cur)
        return (
          <span className="oe-ref">
            <button type="button" tabIndex={-1} className="sui-btn" aria-label={`Previous ${title(r)}`} onClick={() => stepAt(-1)}>
              ◂
            </button>
            <span data-testid={`order-ref-${r.field}`}>{name}</span>
            <button type="button" tabIndex={-1} className="sui-btn" aria-label={`Next ${title(r)}`} onClick={() => stepAt(1)}>
              ▸
            </button>
          </span>
        )
      }
      case 'multi': {
        const chosen = listOf(d, r.field)
        const choices = choicesOf(opts, d.order, r.field)
        return (
          <span className="oe-chips">
            {choices.length === 0 ? <span className="oe-dim">None available</span> : null}
            {choices.map((c, i) => {
              const at = chosen.indexOf(c.id)
              return (
                <button
                  key={c.id}
                  type="button"
                  tabIndex={-1}
                  className="sui-btn oe-chip"
                  aria-pressed={at >= 0}
                  {...focusAttr(focused && i === s.pick)}
                  onClick={() => p.dispatch({ type: 'toggleOption', field: r.field, id: c.id })}
                >
                  {r.field === 'priority' && at >= 0 ? `${at + 1}. ` : ''}
                  {c.name}
                </button>
              )
            })}
            {chosen.length === 0 && (r.field === 'wares' || r.field === 'systems') ? <span className="oe-dim">{r.field === 'wares' ? 'Any' : 'Jumps from home'}</span> : null}
          </span>
        )
      }
      case 'action': {
        const text = r.action === 'save' ? (s.saving ? 'Saving…' : dirty ? 'Save order' : 'Saved') : r.action === 'revert' ? 'Revert' : 'Back to fleet'
        return (
          <button
            type="button"
            tabIndex={-1}
            className={`sui-btn oe-action oe-action--${r.action}`}
            data-testid={`order-${r.action}`}
            disabled={r.action === 'save' && (issues.length > 0 || s.saving)}
            onClick={() => p.dispatch({ type: r.action })}
          >
            {text}
          </button>
        )
      }
    }
  }

  return (
    <section className="oe" aria-label="Standing order" data-testid="order-editor">
      <h3 className="sd-h">Standing order{s.preset !== null ? ` · ${presets.find((x) => x.id === s.preset)?.name ?? ''}` : ''}</h3>
      <div className="oe-rows" role="list">
        {rows.map((r, i) => {
          const focused = i === s.cursor
          const key = rowKey(r)
          const field = r.kind === 'number' ? r.param : r.kind === 'stopDay' ? 'stopDay' : 'field' in r ? r.field : r.kind
          return (
            <div
              key={key}
              role="listitem"
              className="oe-row"
              data-testid={`order-row-${key}`}
              data-invalid={bad.has(field) ? '' : undefined}
              {...focusAttr(focused)}
              onClick={() => p.dispatch({ type: 'cursor', index: i })}
            >
              <span className="oe-label">{title(r)}</span>
              <span className="oe-value">{value(r, focused, i)}</span>
            </div>
          )
        })}
      </div>
      <p className="oe-notice" role="status" aria-live="polite" data-testid="order-notice">
        {s.notice ?? (issues.length > 0 ? issues[0].message : '')}
      </p>
    </section>
  )
}
